import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  theme,
  Tooltip,
  Alert,
  Typography,
  Tag,
  Space
} from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect } from 'react'
import { useMedicalAidPayment } from '../hooks'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'
import EnhancedAllocationModal from './EnhancedAllocationModal'
import { formatCurrency } from '../../../../Billing-Module-Enhanced/utils/helpers'

const { Text } = Typography

/**
 * Component for displaying and managing medical aid payment form
 * @param {Object} data - Initial data for the medical aid payment
 * @param {Function} handleGetRemittances - Function to refresh remittances
 */
const MedicalAidPaymentForm = ({ data, handleGetRemittances }) => {
  const [form] = Form.useForm()
  const { token } = theme.useToken()
  const [enhancedAllocationModal, setEnhancedAllocationModal] = useState({
    visible: false,
    invoice: null
  })

  // Initialize the medical aid payment hook
  const {
    loading,
    submitLoading,
    items,
    searchData,
    searchLoading,
    invoices,
    isModalVisible,
    setIsModalVisible,
    handleAllocatePayment,
    handleCloseTab,
    handleFieldChange,
    handleTableFieldChange,
    generateTotalsData,
    handleSearch,
    handleAccountChange,
    handleInvoiceSelect,
    setItems
  } = useMedicalAidPayment(data, handleGetRemittances, form)

  useEffect(() => {
    const paymentAmount = form.getFieldValue('paymentAmount')
    const total = items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.total || 0)
      const itemMedicalAid = parseFloat(item.medicalAid || 0)
      const itemPatientBalance = parseFloat(item.patientBalance || 0)
      const itemPayment = parseFloat(item.payment || 0)
      const itemDiscount = parseFloat(item.discount || 0)

      return sum + itemTotal - itemMedicalAid - itemPatientBalance + itemPayment - itemDiscount
    }, 0)

    form.setFieldsValue({
      total: formatCurrency(total),
      runningTotal: formatCurrency(total)
    })
  }, [items, form.getFieldValue('paymentAmount')])

  return (
    <Row>
      <Col span={24}>
        <Card
          size='small'
          title={<span style={{ color: token.colorPrimary, fontSize: 18 }}>New Medical Aid Payment</span>}
        >
          <Card
            size='small'
            bordered={false}
            title={<span style={{ color: token.colorSecondary, fontSize: 18 }}>Account Details</span>}
            type='inner'
          >
            <Form
              form={form}
              initialValues={{
                ...data,
                receiptDate: dayjs(new Date())
              }}
              onValuesChange={handleFieldChange}
              layout='vertical'
            >
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item name='medicalAid' label='Medical Aid'>
                        <Input readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name='receiptNumber' label='Receipt Number'>
                        <Input readOnly />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item name='total' label='Total'>
                        <Input addonBefore='R' readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name='runningTotal' label='Running Total'>
                        <Input addonBefore='R' readOnly />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item
                        name='account'
                        label='Account'
                        rules={[{ required: true, message: 'Please select an Account' }]}
                      >
                        <Select
                          style={{ width: '100%' }}
                          showSearch
                          allowClear
                          placeholder='Search an Account'
                          onSearch={(value) => {
                            handleSearch(value)
                          }}
                          onChange={(value) => {
                            handleAccountChange(value)
                          }}
                          filterOption={false}
                          notFoundContent={
                            searchLoading ? (
                              <AgiliteSkeleton skActive spinnerTip='Searching...' />
                            ) : (
                              <Row>
                                <Col span={24}>
                                  <p style={{ textAlign: 'center' }}>No data found</p>
                                </Col>
                                <Col span={24}>
                                  <h4 style={{ textAlign: 'center' }}>Type a Patient Name to start searching</h4>
                                </Col>
                              </Row>
                            )
                          }
                          options={searchData.map((item) => {
                            return {
                              key: item._id,
                              label: `(${item.idNo ? item.idNo : 'No ID'}) - ${item.firstName} ${item.lastName}`,
                              value: `${item._id}`
                            }
                          })}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name='receiptDate'
                        label='Receipt Date'
                        rules={[{ required: true, message: 'Please select a Receipt Date' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item
                        name='accountStatus'
                        label='Account Status'
                        rules={[{ required: true, message: 'Please select an Account Status' }]}
                      >
                        <Select
                          options={[
                            { label: 'Normal', value: 'normal' },
                            { label: 'Suspended', value: 'suspended' },
                            { label: 'Hand Over', value: 'handOver' },
                            { label: 'Bad Debt', value: 'badDebt' }
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name='paymentAmount'
                        label='Payment Amount'
                        rules={[{ required: true, message: 'Please provide a Payment Amount' }]}
                      >
                        <Input type='number' prefix='R' readOnly />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Card>
          <Card
            size='small'
            bordered={false}
            type='inner'
            title={<span style={{ color: token.colorSecondary, fontSize: 18 }}>Items</span>}
          >
            <Table
              pagination={false}
              size='small'
              loading={loading}
              tableLayout='fixed'
              columns={[
                { title: 'Item Type', dataIndex: 'type', key: 'type', width: '8%' },
                { title: 'Code', dataIndex: 'code', key: 'code', width: '5%' },
                { title: 'Description', dataIndex: 'name', key: 'description', width: '40%' },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#ddd', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Medical Aid',
                  dataIndex: 'medicalAid',
                  key: 'medicalAid',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#FCA19C', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Patient Liable',
                  dataIndex: 'patientBalance',
                  key: 'patient',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#ddd', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Payment',
                  dataIndex: 'payment',
                  key: 'payment',
                  width: '6%',
                  render: (text, record, index) => {
                    return (
                      <InputNumber
                        style={{ width: '100%', textAlign: 'right' }}
                        onKeyDown={(e) => {
                          const tmpItems = JSON.parse(JSON.stringify(items))
                          const currentItem = tmpItems[index]

                          switch (e.key.toUpperCase()) {
                            case 'M':
                              e.preventDefault()
                              if (parseFloat(currentItem.patientBalance) > 0) {
                                currentItem.medicalAid = (
                                  parseFloat(currentItem.medicalAid) + parseFloat(currentItem.patientBalance)
                                ).toFixed(2)
                                currentItem.patientBalance = '0'
                                currentItem.patient = '0'
                                setItems(tmpItems)
                              }
                              break
                            case 'P':
                              e.preventDefault()
                              if (parseFloat(currentItem.medicalAid) > 0) {
                                currentItem.patientBalance = (
                                  parseFloat(currentItem.patientBalance) + parseFloat(currentItem.medicalAid)
                                ).toFixed(2)
                                currentItem.patient = currentItem.patientBalance
                                currentItem.medicalAid = '0'
                                setItems(tmpItems)
                              }
                              break
                            case ' ': // Space key
                              e.preventDefault()
                              if (parseFloat(currentItem.medicalAid) > 0) {
                                currentItem.payment = currentItem.medicalAid
                                currentItem.medicalAid = '0'
                                setItems(tmpItems)
                              }
                              break
                            case 'R':
                              e.preventDefault()
                              currentItem.patientBalance = currentItem.oldPatient
                              currentItem.patient = currentItem.oldPatient
                              currentItem.medicalAid = currentItem.oldMedicalAid
                              currentItem.payment = '0'
                              setItems(tmpItems)
                              break
                            default:
                              break
                          }
                        }}
                        value={text}
                        onChange={(value) => {
                          // Always call handleTableFieldChange, regardless of paymentValue
                          handleTableFieldChange('payment', index, value)
                        }}
                      />
                    )
                  }
                },
                {
                  title: 'Discount',
                  dataIndex: 'discount',
                  key: 'discount',
                  width: '6%',
                  render: (text, record, index) => (
                    <Input
                      style={{ textAlign: 'right' }}
                      value={text}
                      onChange={(e) => handleTableFieldChange('discount', index, e.target.value)}
                    />
                  )
                },
                {
                  title: 'Comments',
                  dataIndex: 'comments',
                  key: 'comments',
                  width: '10%',
                  render: (text, record, index) => (
                    <Input.TextArea
                      rows={1}
                      value={text}
                      onChange={(e) => handleTableFieldChange('comments', index, e.target.value)}
                    />
                  )
                }
              ]}
              dataSource={items}
              footer={() => {
                return (
                  <Table
                    showHeader={false}
                    size='small'
                    pagination={false}
                    columns={[
                      { width: '8%' },
                      { width: '5%' },
                      { width: '40%', render: () => <b style={{ float: 'right' }}>Total</b> },
                      {
                        width: '6%',
                        dataIndex: 'total',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'medicalAid',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'patient',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'payment',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'discount',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      { width: '10%' }
                    ]}
                    dataSource={generateTotalsData()}
                  />
                )
              }}
            />
          </Card>
        </Card>
        <Row justify='space-around' style={{ marginTop: 10 }}>
          <Col>P = Move to Patient Liable</Col>
          <Col>M = Move to Medical Aid</Col>
          <Col>R = Reset</Col>
          <Col>Space - Allocate</Col>
        </Row>
        <Row justify='center' style={{ marginTop: 10 }} gutter={[12, 12]}>
          <Col>
            <Button
              type='primary'
              disabled={items.length === 0}
              onClick={() => handleAllocatePayment(form)}
              loading={submitLoading}
            >
              Allocate Payment
            </Button>
          </Col>
          <Col>
            <Button danger onClick={() => handleCloseTab()}>
              Close
            </Button>
          </Col>
        </Row>
      </Col>
      <Modal
        title='Select Invoice'
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1200}
      >
        <Alert
          message='Invoice Selection'
          description='Select an invoice to allocate the medical aid payment. The table shows the outstanding amounts for each invoice.'
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          size='small'
          pagination={false}
          columns={[
            {
              title: 'Invoice No',
              dataIndex: 'invoiceNo',
              key: 'invoiceNo',
              width: '10%'
            },
            {
              title: 'Date',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: '15%',
              render: (text) => dayjs(text).format('YYYY-MM-DD')
            },
            {
              title: 'Patient',
              key: 'patient',
              width: '15%',
              render: (_, record) => {
                const patient = searchData.find((p) => p._id === form.getFieldValue('account'))
                return patient ? `${patient.firstName} ${patient.lastName}` : '-'
              }
            },
            {
              title: 'Total',
              key: 'total',
              width: '10%',
              align: 'right',
              render: (_, record) => {
                const total =
                  record.billingRecords?.reduce((sum, item) => {
                    const amount = parseFloat(item.total || 0)
                    return sum + amount
                  }, 0) || 0
                return formatCurrency(total)
              }
            },
            {
              title: 'Medical Aid',
              key: 'medicalAid',
              width: '10%',
              align: 'right',
              render: (_, record) => {
                const medicalAid =
                  record.billingRecords?.reduce((sum, item) => {
                    // Handle both field names: medicalAid and medicalAidDue
                    const amount = parseFloat(item.medicalAid || item.medicalAidDue || 0)
                    return sum + amount
                  }, 0) || 0
                return <Text type='primary'>{formatCurrency(medicalAid)}</Text>
              }
            },
            {
              title: 'Already Paid',
              key: 'paid',
              width: '10%',
              align: 'right',
              render: (_, record) => {
                // Calculate from existing payments in remittance
                const paid =
                  data.payments
                    ?.filter((p) => p.invoiceRef === record._id)
                    .reduce((sum, p) => sum + parseFloat(p.paymentAmount || 0), 0) || 0
                return <Text type='success'>{formatCurrency(paid)}</Text>
              }
            },
            {
              title: 'Outstanding',
              key: 'outstanding',
              width: '10%',
              align: 'right',
              render: (_, record) => {
                const medicalAid =
                  record.billingRecords?.reduce((sum, item) => {
                    // Handle both field names: medicalAid and medicalAidDue
                    const amount = parseFloat(item.medicalAid || item.medicalAidDue || 0)
                    return sum + amount
                  }, 0) || 0
                const paid =
                  data.payments
                    ?.filter((p) => p.invoiceRef === record._id)
                    .reduce((sum, p) => sum + parseFloat(p.paymentAmount || 0), 0) || 0
                const outstanding = medicalAid - paid

                return (
                  <Text type={outstanding > 0 ? 'danger' : 'success'} strong>
                    {formatCurrency(outstanding)}
                  </Text>
                )
              }
            },
            {
              title: 'Status',
              key: 'status',
              width: '10%',
              align: 'center',
              render: (_, record) => {
                const medicalAid =
                  record.billingRecords?.reduce((sum, item) => {
                    // Handle both field names: medicalAid and medicalAidDue
                    const amount = parseFloat(item.medicalAid || item.medicalAidDue || 0)
                    return sum + amount
                  }, 0) || 0
                const paid =
                  data.payments
                    ?.filter((p) => p.invoiceRef === record._id)
                    .reduce((sum, p) => sum + parseFloat(p.paymentAmount || 0), 0) || 0
                const outstanding = medicalAid - paid

                if (outstanding <= 0) {
                  return <Tag color='green'>Fully Paid</Tag>
                } else if (paid > 0) {
                  return <Tag color='orange'>Partial</Tag>
                }
                return <Tag color='red'>Unpaid</Tag>
              }
            },
            {
              title: 'Action',
              key: 'action',
              width: '10%',
              align: 'center',
              render: (_, record) => (
                <Button
                  type='primary'
                  onClick={() => {
                    setIsModalVisible(false)
                    setEnhancedAllocationModal({
                      visible: true,
                      invoice: record
                    })
                  }}
                >
                  Allocate
                </Button>
              )
            }
          ]}
          dataSource={invoices}
        />
      </Modal>

      {/* Enhanced Allocation Modal */}
      {enhancedAllocationModal.invoice && (
        <EnhancedAllocationModal
          visible={enhancedAllocationModal.visible}
          onCancel={() => setEnhancedAllocationModal({ visible: false, invoice: null })}
          invoice={enhancedAllocationModal.invoice}
          totalAmountToAllocate={parseFloat(form.getFieldValue('paymentAmount') || 0)}
          existingAllocations={data.payments || []}
          onAllocate={async (invoice, allocations, totalAmount) => {
            // First select the invoice to set invoiceRef in the hook
            await handleInvoiceSelect(invoice)

            // Update the form with the allocated data
            const billingRecords = allocations.map((alloc) => ({
              ...alloc.billingRecord,
              payment: alloc.amount,
              discount: 0,
              comments: ''
            }))

            setItems(billingRecords)
            form.setFieldsValue({
              invoiceRef: invoice._id,
              invoiceNo: invoice.invoiceNo
            })

            // Close the modal
            setEnhancedAllocationModal({ visible: false, invoice: null })

            // Call the original allocation handler after a small delay to ensure state is updated
            setTimeout(async () => {
              await handleAllocatePayment(form)
            }, 100)
          }}
        />
      )}
    </Row>
  )
}

export default MedicalAidPaymentForm
